import {createApi, fetchBaseQuery, retry} from '@reduxjs/toolkit/query/react'
import {getWordpressVars} from "@lib/wordpressVars";
import {Middleware, MiddlewareAPI} from "redux";
import {isRejectedWithValue} from '@reduxjs/toolkit';
import {NotificationType, showNotification} from "@lib/notification";
import {FetchBaseQueryArgs} from "@reduxjs/toolkit/dist/query/fetchBaseQuery";
import {formatTitleCase} from "@lib/format";
import {StatusCodes} from '@lib/constants';
import React from "react";
import {Modal} from "antd";
import {WarningOutlined} from "@ant-design/icons";
import { decode } from 'html-entities';

export interface PaginationResponse {
  current_page: number;
  next_page: number;
  prev_page: number;
  per_page: number;
  total_items: number;
  max_pages: number;
}

export interface PaginationQuery {
  page: number;
  perPage: number;
}

export interface DateRange {
  id?: number;
  start: string;
  end: string;
}

export const isPaginatedQuery = (checkObj: any): checkObj is PaginationQuery => {
  return !!(checkObj as PaginationQuery).page;
}

export const shapeBlockData = (blocks) => {
  return blocks?.map((block) => {

    const items = block.attrs
      ?.items
      ?.map((item) => {
        return {
          ...item,
          title: item?.title ? decode(item.title) : null,
          description: item?.description 
            ? decode(item.description) 
            : null,
        }
      })

    const results = block.results
      ?.map((item) => {

        const detailTitle = item?.details?.title
          ? decode(item.details.title)
          : null;

        const detailDescription = item?.details?.description
          ? decode(item.details.description)
          : null;

        return {
          ...item,
          details: {
            ...item.details,
            title: detailTitle,
            description: detailDescription,
          },
          title: item?.title ? decode(item.title) : null,
          description: item?.description ? decode(item.description) : null,
        }
      });

    return {
      ...block,
      results,
      attrs: {
        ...block.attrs,
        items,
      },
      title: decode(block.title),
    }
  })
}

export const shapeRelatedPostsData = (posts) => {
  return posts?.map((post) => {
    return {
      ...post,
      title: decode(post.title),
    }
  })
}

// Create our baseQuery instance
const baseQuery = fetchBaseQuery({
  baseUrl: getWordpressVars().root,
  prepareHeaders: (headers) => {
    const nonce = getWordpressVars().nonce;
    if (nonce) {
      headers.set('X-WP-Nonce', nonce)
    }
    return headers
  },
} as FetchBaseQueryArgs)

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 2 })

export const api = createApi({
  reducerPath: 'splitApi',
  baseQuery: baseQueryWithRetry,

  /**
   * Tag types must be defined in the original API definition
   * for any tags that would be provided by injected endpoints
   */
  tagTypes: [
    'Posts',
    'Issue',
    'Publisher',
    'Overview',
    'Account',
    'Boxes',
    'Contact',
    'Series',
    'PartnerPortal',
    'ExportableFields',
    'ExportableFieldsSetActive',
    'PartnerExportIssueExclusion',
    'PartnerNewReleasesDownload',
    'PartnerSubscriptions',
    'Ads',
  ],

  endpoints: () => ({}),
})

export const enhancedApi = api.enhanceEndpoints({
  endpoints: () => ({
    getPost: () => 'test',
  }),
})

export const apiShowError: Middleware = (api: MiddlewareAPI) => (next) => (action) => {
  if (isRejectedWithValue(action)) {
    if (action.payload.data.code === StatusCodes.COOKIE_INVALID_NONCE) {
      showNotification("Sorry, you have been logged out. Please log back in.", NotificationType.ERROR);
      setTimeout(() => window.location.href = '/', 5000);
    } else if (
      action.payload.data.code === StatusCodes.NO_POSTS_FOUND ||
      action.payload.data.code === StatusCodes.NO_PAGES_FOUND ||
      action.payload.data.code === StatusCodes.NO_ISSUES_FOUND
    ) {
      showNotification(`${action.payload.data.message}`, NotificationType.INFO);
    } else {
      console.log("Unhandled error", action.payload.data);
      showNotification("Sorry, there was an error. Please contact help@covrprice.com to let us know, thanks.", NotificationType.ERROR);
    }
  }
  return next(action);
}

export const showResponse = (response, successMessage, errorMessage) => {

  if (!response.isLoading && response.data) {
    if (response.data.success === 1) if(successMessage) showNotification(successMessage, NotificationType.SUCCESS);
    if (response.data.success === 0){
        if(response.data.error === 'max_boxes_reached' ){
        Modal.confirm({
          autoFocusButton: null,
          icon: <WarningOutlined />,
          content: (
            <div>
              <div><b>Max Quantity Reached</b></div>
              <div className="pad-top-10">
                {(response.data.type === 'Basic' || response.data.type === 'Standard') ? (
                  <>
                    Sorry, you're not allowed to create Virtual Boxes with your current membership.
                    To gain access to this feature, please upgrade to our Unlimited membership
                  </>
                ) : (
                  <>
                    Sorry, you're only allowed {response.data.total} Virtual Boxes
                  </>
                )}
              </div>
            </div>
          ),
          okCancel: (response.data.type === 'Basic' || response.data.type === 'Standard'),
          okText: (response.data.type === 'Basic' || response.data.type === 'Standard') ? 'Upgrade' : undefined,
          onOk: (response.data.type === 'Basic' || response.data.type === 'Standard') ? () => {
            window.location.href = '/plans/unlimited'
          } : undefined,
        });
      } else if(response.data.error === 'max_issues_reached' ){
        Modal.confirm({
          autoFocusButton: null,
          icon: <WarningOutlined />,
          content: (
            <div>
              <div><b>Max Quantity Reached</b></div>
              <div className="pad-top-10">
              Sorry, you're only allowed {response.data.total} comics with the {response.data.type} account.<br/>
                                You have {response.data.leftover} left.<br/>
                                UPGRADE for more options.
              </div>
            </div>
          ),
          okText: "UPGRADE",
          onOk() {
            window.location.href = "/plans/unlimited/";
          },
        });
      } else {
        showNotification(errorMessage, NotificationType.ERROR);
      }
    }
  }
};


//Dummy function to show signature select.
export interface SignatureValue {
  label: string;
  value: string;
}
export const fetchSignedByList = async (search, count=5): Promise<SignatureValue[]> => {
  return fetch(`/wp-json/covr/v1/user/signed_by?search=${search}&count=${count}`, {
      headers: {
      "Content-Type": "application/json",
      'X-WP-Nonce': getWordpressVars().nonce,
    },
  })
    .then((response) => response.json())
    .then((body) => {
      return body.map(
        (item: { id: number, name: string }) => ({
          label: formatTitleCase(item.name),
          value: item.id,
        }),
      );
    })
    .catch(error => {
      showNotification("Sorry, you have been logged out. Please log back in.", NotificationType.ERROR);
      setTimeout(() => window.location.href = '/login', 5000);
    });
}

let wasLoggedIn = false;
const checkApiNonce = async () => {
  return fetch(`/wp-json/covr/v1/issues/home-page/new-releases`, {
    headers: {
      "Content-Type": "application/json",
      'X-WP-Nonce': getWordpressVars().nonce,
    },
  })
    .then(async (response) => {
      const nonceData = await response.json();
      if (nonceData.code === StatusCodes.COOKIE_INVALID_NONCE) {
        //Nonce has failed, reload page.
        window.location.reload();
      } else {
        //Nonce is valid. Check user.
        return fetch(`/wp-json/covr/v1/user/logged_in`, {
          headers: {
            "Content-Type": "application/json",
            'X-WP-Nonce': getWordpressVars().nonce,
          },
        })
          .then(async (response) => {
            const userData = await response.json();
            if (userData.user !== null) {
              // User is logged in.
              wasLoggedIn = true;
            } else {
              // User is not logged in.
              if (wasLoggedIn) {
                //User was logged in, now logged out. Redirect to home.
                window.location.href = '/';
              }
            }
            return true;
          });
      }
    })
    .catch(error => {
      //Nonce has failed, reload page.
      window.location.reload();
    });
};

const keepAlive = () => {
  setInterval(async () => {
    await checkApiNonce();
  }, (1000 * 60 * 30))
};
keepAlive();